import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getOrdiniDaFatturare, createFattureBulk, getFattureBulkPreview } from '../services/api';
import BulkPreviewFatturaModal from '../components/modals/BulkPreviewFatturaModal';
import { Table, Form, Spinner, Alert, Row, Col, Pagination, Card, Button, Badge } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import MobileListLayout from '../common/MobileListLayout';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';

const columnHelper = createColumnHelper();

const allColumnsMap = {
    id: "ID Ordine",
    cliente_display: "Cliente",
    data_ordine: "Data Ordine",
    totale_ordine: "Totale Finale",
    origine: "Origine",
    numero_ordine_marketplace: "Numero Ordine Marketplace",
};

function OrdiniDaFatturareList() {
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [ordini, setOrdini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedOrdineIds, setSelectedOrdineIds] = useState([]);

  // New states for bulk preview
  const [showBulkPreviewModal, setShowBulkPreviewModal] = useState(false);
  const [bulkPreviewData, setBulkPreviewData] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Stati per @tanstack/react-table
  const [sorting, setSorting] = useState([{ id: 'data_ordine', desc: true }]);
  const [columnFilters, setColumnFilters] = useState([]);
  
  // Paginazione
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalOrdini, setTotalOrdini] = useState(0);

  const fetchOrdiniData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = JSON.stringify(columnFilters.reduce((acc, filter) => { acc[filter.id] = filter.value; return acc; }, {}));
      const sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
      const response = await getOrdiniDaFatturare(
        (currentPage - 1) * itemsPerPage,
        itemsPerPage,
        sortParams,
        filters
      );
      setOrdini(response.data.ordini);
      setTotalOrdini(response.data.total);
    } catch (err) {
      setError('Errore nel caricamento degli ordini da fatturare.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, columnFilters]);

  useEffect(() => {
    fetchOrdiniData();
  }, [fetchOrdiniData]);
  
  const handleConfirmBulkCreate = async () => {
    setIsCreating(true);
    setError(null);
    try {
        const response = await createFattureBulk(selectedOrdineIds);
        const { success, failed } = response.data;
        let alertMessage = `${success.length} fatture create con successo.`;
        if (failed.length > 0) {
            const failedDetails = failed.map(f => `Ordine ID ${f.ordine_id}: ${f.error}`).join('\n');
            alertMessage += `\n\n${failed.length} fatture non create:\n${failedDetails}`;
        }
        alert(alertMessage);
        fetchOrdiniData(); // Refresh data
        table.toggleAllRowsSelected(false); // Deselect
    } catch (err) {
        const errorMsg = err.response?.data?.detail || "Errore durante la creazione massiva delle fatture.";
        setError(errorMsg);
        alert(`Errore: ${errorMsg}`);
    } finally {
        setIsCreating(false);
    }
  };

  // Le fatture definitive vanno a Fattura24/SDI: senza passare dall'anteprima si chiede conferma esplicita
  const chiediConfermaECrea = () => {
    const totale = selectedOrdersTotal.toFixed(2).replace('.', ',');
    if (window.confirm(`Emettere ${selectedOrdineIds.length} fatture definitive (totale € ${totale})?
Le fatture vengono inviate allo SDI e non si possono annullare da qui.`)) {
      handleConfirmBulkCreate();
    }
  };

  const handlePreviewSelected = async () => {
    setLoadingPreview(true);
    setBulkPreviewData([]);
    try {
      const response = await getFattureBulkPreview(selectedOrdineIds);
      setBulkPreviewData(response.data);
      setShowBulkPreviewModal(true);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Errore durante la generazione dell'anteprima massiva.";
      alert(`Errore: ${errorMsg}`);
      console.error(err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const selectedOrdersTotal = useMemo(() => {
    return ordini
      .filter(o => selectedOrdineIds.includes(o.id))
      .reduce((sum, o) => sum + o.totale_ordine, 0);
  }, [selectedOrdineIds, ordini]);

  // Helper functions for mobile view
  const formatDate = (dateString) => {
    if (!dateString) return 'N/D';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '€0,00';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(parseFloat(amount));
  };

  const renderOrdineCard = useCallback((ordine) => {
    const cliente = ordine.cliente;
    const clienteDisplay = cliente?.denominazione_sociale || 
                          [cliente?.nome, cliente?.cognome].filter(Boolean).join(' ') ||
                          'Cliente non specificato';
    
    return (
      <Card className="mb-2" style={{ fontSize: '0.9rem' }}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <strong style={{ fontSize: '1rem' }}>Ordine #{ordine.id}</strong>
              {ordine.numero_ordine_marketplace && (
                <Badge bg="info" className="ms-2">MP: {ordine.numero_ordine_marketplace}</Badge>
              )}
            </div>
            <div className="text-end">
              <strong style={{ fontSize: '1.1rem', color: '#28a745' }}>
                {formatCurrency(ordine.totale_ordine)}
              </strong>
            </div>
          </div>

          <div className="mb-2">
            <small className="text-muted">Cliente:</small>
            <div>{clienteDisplay}</div>
          </div>

          <Row className="mb-2">
            <Col xs={6}>
              <small className="text-muted">Data Ordine:</small>
              <div>{formatDate(ordine.data_ordine)}</div>
            </Col>
            <Col xs={6}>
              <small className="text-muted">Origine:</small>
              <div>
                <Badge bg="secondary">{ordine.origine || 'N/D'}</Badge>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  }, []);

  const actionsDropdownContent = useMemo(() => (
    <>
      <p className="text-muted px-3 mb-2" style={{ fontSize: '0.85rem' }}>
        Ordini selezionati: {selectedOrdineIds.length} - Totale: {formatCurrency(selectedOrdersTotal)}
      </p>
      <Button
        variant="primary"
        className="w-100 mb-2 mx-3"
        style={{ maxWidth: 'calc(100% - 24px)' }}
        onClick={handlePreviewSelected}
        disabled={selectedOrdineIds.length === 0 || loadingPreview}
      >
        {loadingPreview ? 'Caricamento...' : `Anteprima Fatture (${selectedOrdineIds.length})`}
      </Button>
      <Button
        variant="warning"
        className="w-100 mx-3"
        style={{ maxWidth: 'calc(100% - 24px)' }}
        onClick={chiediConfermaECrea}
        disabled={selectedOrdineIds.length === 0 || isCreating}
      >
        {isCreating ? 'Creando...' : `Crea Fatture Definitive (${selectedOrdineIds.length})`}
      </Button>
    </>
  ), [selectedOrdineIds.length, selectedOrdersTotal, loadingPreview, isCreating, handlePreviewSelected, chiediConfermaECrea]);

  const getColumnValue = (ordine, columnName) => {
    switch (columnName) {
      case 'cliente_display':
        if (!ordine.cliente) return 'N/A';
        const { nome, cognome, denominazione_sociale } = ordine.cliente;
        const namePart = [nome, cognome].filter(Boolean).join(' ');
        return denominazione_sociale || namePart || 'Cliente non specificato';
      case 'data_ordine':
        return ordine.data_ordine ? new Date(ordine.data_ordine).toLocaleDateString() : 'N/A';
      case 'totale_ordine':
        return `€ ${parseFloat(ordine.totale_ordine || 0).toFixed(2)}`;
      default:
        return ordine[columnName];
    }
  };

  const columns = useMemo(
    () => [
        { id: 'select', header: ({ table }) => (<Form.Check type="checkbox" onChange={table.getToggleAllRowsSelectedHandler()} checked={table.getIsAllRowsSelected()} />), cell: ({ row }) => (<Form.Check type="checkbox" checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} onClick={(e) => e.stopPropagation()} />) },
      ...Object.entries(allColumnsMap).map(([key, value]) =>
        columnHelper.accessor(key, { header: value, cell: info => getColumnValue(info.row.original, key) })
      ),
    ],
    []
  );

  const table = useReactTable({
    data: ordini,
    columns,
    state: { sorting, columnFilters },
    // Selezione per ID ordine, non per posizione: cambiando pagina o ordinamento resta sugli ordini scelti
    getRowId: (row) => String(row.id),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualFiltering: true,
    manualSorting: true,
  });

  const { rows } = table.getSelectedRowModel();
  useEffect(() => {
    setSelectedOrdineIds(rows.map(row => row.original.id));
  }, [rows]);

  return (
    <>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="h3">Ordini da Fatturare</h1>
          <Badge bg="secondary">{totalOrdini} ordini pronti</Badge>
        </Col>
      </Row>
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col md={6}>
              <Button 
                onClick={handlePreviewSelected} 
                disabled={loadingPreview || isCreating || selectedOrdineIds.length === 0} 
                variant="info" 
                className="me-2"
              >
                {loadingPreview ? <><Spinner as="span" animation="border" size="sm" /> Caricamento Anteprima...</> : `Anteprima Fatture Selezionate (${selectedOrdineIds.length})`}
              </Button>
              <Button 
                onClick={chiediConfermaECrea}
                disabled={isCreating || selectedOrdineIds.length === 0}
                variant="success"
                className="me-2"
              >
                {isCreating ? <><Spinner as="span" animation="border" size="sm" /> Creazione...</> : `Crea Fatture Selezionate (${selectedOrdineIds.length})`}
              </Button>
            </Col>
            <Col md={6} className="d-flex justify-content-end align-items-center">
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {loading && <div className="text-center"><Spinner animation="border" /></div>}
          {error && <Alert variant="danger">{error}</Alert>}
          
          {!loading && !error && (
            <>
              {isMobile ? (
                <MobileListLayout
                  items={ordini}
                  renderCard={renderOrdineCard}
                  entityName="ordine"
                  entityNamePlural="ordini"
                  showCreateButton={false}
                  selectedIds={selectedOrdineIds}
                  onToggleSelect={(ordineId) => {
                    const row = table.getRowModel().rows.find(r => r.original.id === ordineId);
                    if (row) row.toggleSelected();
                  }}
                  actionsDropdownContent={actionsDropdownContent}
                  showSelectAll={true}
                  onSelectAll={() => table.toggleAllRowsSelected()}
                  isAllSelected={table.getIsAllRowsSelected()}
                />
              ) : (
                <Table striped bordered hover responsive>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        style={{cursor: header.column.getCanSort() ? 'pointer' : 'default'}}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        {{asc: ' 🔼', desc: ' 🔽'}[header.column.getIsSorted()] ?? null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>{table.getRowModel().rows.map(row => (<tr key={row.id} onClick={() => row.toggleSelected()} className={row.getIsSelected() ? 'table-primary' : ''} style={{ cursor: 'pointer' }}>{row.getVisibleCells().map(cell => (<td key={cell.id} onClick={cell.column.id === 'azioni' ? (e) => e.stopPropagation() : undefined}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>))}</tr>))}</tbody>
            </Table>
              )}
            </>
          )}
        </Card.Body>
        <Card.Footer className="d-flex justify-content-end align-items-center">
          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
            <Pagination.Item>{currentPage}</Pagination.Item>
            <Pagination.Next onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === Math.ceil(totalOrdini / itemsPerPage)} />
            <Pagination.Last onClick={() => setCurrentPage(Math.ceil(totalOrdini / itemsPerPage))} disabled={currentPage === Math.ceil(totalOrdini / itemsPerPage)} />
          </Pagination>
        </Card.Footer>
      </Card>

      <BulkPreviewFatturaModal
        show={showBulkPreviewModal}
        handleClose={() => setShowBulkPreviewModal(false)}
        previews={bulkPreviewData}
        onConfirm={handleConfirmBulkCreate}
        loadingConfirm={isCreating}
        confirmError={error} // Passa l'errore di creazione, se presente
        selectedOrdersTotal={selectedOrdersTotal}
      />
    </>
  );
}

export default OrdiniDaFatturareList;

